import React from 'react';
import { Helmet } from 'react-helmet';

/**
 * SEO Component for VA Profile Pages
 * Provides comprehensive meta tags, Open Graph, Twitter Cards, and structured data
 * @param {Object} profile - The VA profile data
 * @param {boolean} isPublicView - Whether this is a public profile view
 */
const ProfileSEO = ({ profile, isPublicView = false }) => {
  if (!profile) return null;

  // Generate SEO-friendly title
  const pageTitle = profile.title 
    ? `${profile.full_name} - ${profile.title} | VA Sprouts`
    : `${profile.full_name} - Virtual Assistant | VA Sprouts`;

  // Generate description from bio or default
  const description = profile.bio 
    ? profile.bio.substring(0, 160) + (profile.bio.length > 160 ? '...' : '')
    : `Hire ${profile.full_name}, an experienced virtual assistant with ${profile.experience_years || 0}+ years of experience. ${profile.skills?.slice(0, 5).join(', ') || 'Professional VA services'}.`;

  // Generate keywords
  const keywords = [
    profile.full_name,
    'virtual assistant',
    'VA services',
    ...(profile.skills || []).slice(0, 10),
    ...(profile.industry_specialization || []).slice(0, 5),
    profile.location,
    profile.experience_level,
  ].filter(Boolean).join(', ');

  // Generate canonical URL
  const canonicalUrl = profile.public_username
    ? `${window.location.origin}/va/${profile.public_username}`
    : `${window.location.origin}/va/${profile.id}`;

  // Profile image for Open Graph
  const profileImage = profile.profile_picture || `${window.location.origin}/images/default-avatar.png`;

  // Generate structured data for Person schema
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": profile.full_name,
    "url": canonicalUrl,
    "image": profileImage,
    "jobTitle": profile.title || "Virtual Assistant",
    "description": profile.bio || description,
    "knowsAbout": profile.skills || [],
    "workLocation": {
      "@type": "Place",
      "address": profile.location || "Remote"
    },
    ...(profile.hourly_rate && {
      "priceRange": `$${profile.minimum_rate || profile.hourly_rate}-$${profile.maximum_rate || profile.hourly_rate}`
    }),
    ...(profile.rating && {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": profile.rating,
        "reviewCount": profile.reviewCount || profile.review_count || 0,
        "bestRating": 5,
        "worstRating": 1
      }
    }),
    ...(profile.linkedin && {
      "sameAs": [profile.linkedin]
    }),
    ...(profile.website && {
      "url": profile.website
    })
  };

  // Generate breadcrumb structured data
  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Virtual Assistants",
        "item": `${window.location.origin}/candidate-search`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": profile.full_name,
        "item": canonicalUrl
      }
    ]
  };

  // Generate service structured data if skills are available
  const serviceData = profile.skills && profile.skills.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Virtual Assistant Services",
    "provider": {
      "@type": "Person",
      "name": profile.full_name,
      "image": profileImage,
      "url": canonicalUrl
    },
    "areaServed": "Worldwide",
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceUrl": canonicalUrl
    },
    ...(profile.hourly_rate && {
      "offers": {
        "@type": "Offer",
        "price": profile.hourly_rate,
        "priceCurrency": profile.preferred_currency || "USD",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": profile.hourly_rate,
          "priceCurrency": profile.preferred_currency || "USD",
          "unitText": "HOUR"
        }
      }
    }),
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "VA Services",
      "itemListElement": profile.skills.slice(0, 10).map(skill => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": skill
        }
      }))
    }
  } : null;

  // Prepare all meta tags before rendering
  const robotsContent = isPublicView 
    ? "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    : "noindex, nofollow";
  
  const firstName = profile.full_name ? profile.full_name.split(' ')[0] : '';
  const lastName = profile.full_name && profile.full_name.split(' ').length > 1 
    ? profile.full_name.split(' ').slice(1).join(' ') 
    : '';

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots Meta Tags */}
      <meta name="robots" content={robotsContent} />
      {isPublicView && <meta name="googlebot" content="index, follow" />}

      {/* Open Graph Tags */}
      <meta property="og:type" content="profile" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={profileImage} />
      <meta property="og:image:alt" content={`${profile.full_name} - Virtual Assistant Profile Picture`} />
      <meta property="og:site_name" content="VA Sprouts" />
      <meta property="og:locale" content="en_US" />
      {firstName && <meta property="profile:first_name" content={firstName} />}
      {lastName && <meta property="profile:last_name" content={lastName} />}
      {profile.public_username && <meta property="profile:username" content={profile.public_username} />}

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={profileImage} />
      <meta name="twitter:image:alt" content={`${profile.full_name} - Virtual Assistant Profile Picture`} />

      {/* Additional Meta Tags */}
      <meta name="author" content={profile.full_name} />
      <meta name="geo.region" content={profile.location?.split(',')[0] || 'US'} />
      {profile.availability_status && <meta name="availability" content={profile.availability_status} />}

      {/* Structured Data - Person Schema */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>

      {/* Structured Data - Breadcrumb */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbData)}
      </script>

      {/* Structured Data - Service Schema */}
      {serviceData && (
        <script type="application/ld+json">
          {JSON.stringify(serviceData)}
        </script>
      )}

      {/* Additional SEO hints */}
      <meta name="theme-color" content="#10B981" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
    </Helmet>
  );
};

export default ProfileSEO;
